import { Fragment } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { SellInvoiceModelType } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { Modify } from '@teinor/erp'
import { ComponentTabObject } from '../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { ConflictsTy, ConflictsTyComplex, ModalConflictElementSchema } from '../../../../../baseComponents/ModalsLayouts/ModalOrderIncompatibles/types'
import { SellTagsPayload } from '../../../../../utils/reducers/sells/sellTags'
import { OrderData } from '../../../../inputTableLayouts/order/OrderLayout/types'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import { OrderWarningIncompatiblesBillingSellType } from '../../../../inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/OrderWarningIncompatiblesBilling'
import ModalOrderIncompatibles from '../../../../../baseComponents/ModalsLayouts/ModalOrderIncompatibles'
import { WorkingCenterPayload } from '../../../../../utils/reducers/company/workingCenters'
import { InputTableLeftTabsProps } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { ITHeaderOrderTags } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import InputTableLeftColumnForm from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { EmployeesPayload } from '../../../../../utils/reducers/company/employees'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import withLoading from '../../../../../utils/hoc/withLoading'
import { returnClassOfSpanTemplate } from '../../../../../utils/globals/tailwind'
import AppStyleDecider from '../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { useOrderITHeaderHandleChangeLogic } from '../../../../inputTableLayouts/order/useOrderITHeaderHandleChange'

type MultiSellHeaderProps = Modify<InputTableLeftColumnCompleteRequiredProps, ReduxMultiSellHeader & {
    completeData: OrderData | null
    conflicts: ConflictsTyComplex
    sellTags: SellTagsPayload,
    translateText: {
        titleTab: string
        textConflict: string
    }
    urlNavigate: string
}>

const MultiSellHeader: React.FC<MultiSellHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={MultiSellHeaderDefault} NewComponent={MultiSellHeaderNew} {...rest} />
}

const MultiSellHeaderDefault: React.FC<MultiSellHeaderProps> = ({ workingCenters, conflicts, sellTags, translateText, urlNavigate, ...rest }) => {
    const tabs: ComponentTabObject = {
        'main': {
            component: MultiSellDeliveryNoteHeaderMain,
            text: translateText.titleTab,
            extraProps: { workingCenters, sellTags }
        },
    }
    const columnsIncompatibles: ModalConflictElementSchema<ConflictsTy> = [
        {
            dataKey: 'BillingTypeSellOptions',
            customRender: () => <OrderWarningIncompatiblesBillingSellType BillingTypeSellOptions={conflicts.incompatibles?.['BillingTypeSellOptions']} />
        },
    ]
    return (
        <Fragment>
            <InputTableLeftColumn tabs={tabs} {...rest} />
            <ModalOrderIncompatibles incompatibles={conflicts?.incompatibles} urlNavigate={urlNavigate} textConflict={translateText.textConflict} columns={columnsIncompatibles} { ...rest } returnId={rest.completeData?.header?.CustomerId || ''} />
        </Fragment>
    )
}

interface MultiSellDeliveryNoteHeaderMainProps extends InputTableLeftTabsProps {
    workingCenters: WorkingCenterPayload
    sellTags: SellTagsPayload
    headerData: TwinDictionary
}

const MultiSellDeliveryNoteHeaderMain: React.FC<MultiSellDeliveryNoteHeaderMainProps> = ({ workingCenters,  handleChange, headerData, sellTags, setCompleteData }) => {
    const { t } = useTwinTranslation()
    const sections: FormRender<SellInvoiceModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={workingCenters?.[headerData?.WorkingCenterId]?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billingBy', 'Facturado por')} value={workingCenters?.[headerData?.BillingWorkingCenterId]?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={headerData.CurrencyId} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={sellTags || {}} headerData={headerData} onChange={handleChange} className={returnClassOfSpanTemplate(5)} setCompleteData={setCompleteData} readOnly={true} />)
                    },
                    extraProps: { selectedTags: headerData?.tags }
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData}/>
    )
}

const MultiSellHeaderNew: React.FC<MultiSellHeaderProps> = ({ workingCenters, headerData, sellTags, setCompleteData, translateText, conflicts, urlNavigate, getCompleteData, ...rest }) => {
    const {t} = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData: setCompleteData, updateDataURL: rest?.updateDataURL })
    const columnsIncompatibles: ModalConflictElementSchema<ConflictsTy> = [
        {
            dataKey: 'BillingTypeSellOptions',
            customRender: () => <OrderWarningIncompatiblesBillingSellType BillingTypeSellOptions={conflicts.incompatibles?.['BillingTypeSellOptions']} />
        },
    ]
    
    const sections: FormRender<SellInvoiceModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData || {}} />,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={workingCenters?.[headerData?.WorkingCenterId]?.name || ''} />,
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billingBy', 'Facturado por')} value={workingCenters?.[headerData?.BillingWorkingCenterId]?.name || ''} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} />
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={headerData?.CurrencyId} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]

    return (
        <Fragment>
            <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
            <ModalOrderIncompatibles incompatibles={conflicts?.incompatibles} urlNavigate={urlNavigate} textConflict={translateText.textConflict} columns={columnsIncompatibles} {...rest} returnId={rest.completeData?.header?.CustomerId || ''} />
        </Fragment>
    )
}

const MultiSellHeaderDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
    setSellTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
}

export type ReduxMultiSellHeader = ConnectedProps<typeof multiSellHeaderConnect>
const mapMultiSellHeaderConnector = (state: AllReduxPayloads) => ({ employees: state.employees, workingCenters: state.workingCenters,sellTags: state.sellTags})
const multiSellHeaderConnect = connect(mapMultiSellHeaderConnector, MultiSellHeaderDispatch)
const MultiSellHeaderConnectLoading = withLoading(MultiSellHeader, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }, { fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setSellTags' }])
const MultiSellHeaderConnect = multiSellHeaderConnect(MultiSellHeaderConnectLoading)

export default MultiSellHeaderConnect